type AgentMarkProps = {
  agentKey?: string | null;
  size?: number;
  label?: string;
  className?: string;
};

type AgentMarkClusterProps = {
  agentKeys?: readonly string[];
  size?: number;
  label?: string;
  className?: string;
};

type AgentMotif = "lines" | "initial" | "eyes" | "code" | "blank";

type AgentIdentity = {
  color: string;
  motif: AgentMotif;
};

const AGENT_IDENTITIES = [
  { color: "#efb49f", motif: "lines" },
  { color: "#aaa7e8", motif: "initial" },
  { color: "#93cfc3", motif: "eyes" },
  { color: "#a8c5e9", motif: "code" },
  { color: "#dfadc6", motif: "eyes" },
  { color: "#e7c77f", motif: "lines" },
] as const satisfies readonly AgentIdentity[];

const KNOWN_AGENT_IDENTITIES: Readonly<Record<string, AgentIdentity>> = {
  claude: AGENT_IDENTITIES[0],
  grok: AGENT_IDENTITIES[1],
  codex: AGENT_IDENTITIES[2],
  gemini: AGENT_IDENTITIES[3],
  cursor: AGENT_IDENTITIES[4],
  haiku: AGENT_IDENTITIES[5],
  placeholder: { color: "#d8dddf", motif: "blank" },
};

function normalizedAgentKey(value: string | null | undefined): string {
  return value?.trim().toLowerCase() || "agent";
}

function hashAgentKey(key: string): number {
  let hash = 2166136261;
  for (let index = 0; index < key.length; index += 1) {
    hash ^= key.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }
  return hash >>> 0;
}

function agentIdentity(value: string | null | undefined): AgentIdentity {
  const key = normalizedAgentKey(value);
  const knownIdentity = KNOWN_AGENT_IDENTITIES[key];
  if (knownIdentity) return knownIdentity;
  return AGENT_IDENTITIES[hashAgentKey(key) % AGENT_IDENTITIES.length] ?? AGENT_IDENTITIES[0];
}

function Motif({ motif }: { motif: AgentMotif }) {
  if (motif === "blank") return null;
  if (motif === "lines") {
    return (
      <g strokeWidth="1.7">
        <path d="M9 7.25h6M9 10h6M9 12.75h6" />
      </g>
    );
  }
  if (motif === "initial") {
    return <path d="M9.75 7h4.5M12 7v6M9.75 13h4.5" strokeWidth="1.75" />;
  }
  if (motif === "code") {
    return <path d="m10.75 7.5-2.25 2.5 2.25 2.5M13.25 7.5l2.25 2.5-2.25 2.5" strokeWidth="1.65" />;
  }
  return (
    <g fill="currentColor" stroke="none">
      <rect x="9" y="8.7" width="2.1" height="2.6" rx=".7" />
      <rect x="12.9" y="8.7" width="2.1" height="2.6" rx=".7" />
    </g>
  );
}

function Face({ identity, x = 0 }: { identity: AgentIdentity; x?: number }) {
  return (
    <g transform={`translate(${x} 0)`}>
      <rect x="5.4" y="3.15" width="13.2" height="13.7" rx="2.25" fill={identity.color} stroke="none" />
      <path d="M10 2.75H7.8c-1.4 0-2.2.8-2.2 2.2v1.9c0 1.55-.72 2.55-2.2 3.15 1.48.6 2.2 1.6 2.2 3.15v1.9c0 1.4.8 2.2 2.2 2.2H10" />
      <path d="M14 2.75h2.2c1.4 0 2.2.8 2.2 2.2v1.9c0 1.55.72 2.55 2.2 3.15-1.48.6-2.2 1.6-2.2 3.15v1.9c0 1.4-.8 2.2-2.2 2.2H14" />
      <Motif motif={identity.motif} />
    </g>
  );
}

function accessibility(label: string | undefined) {
  return label
    ? { role: "img", "aria-label": label }
    : { "aria-hidden": true as const };
}

export function AgentMark({
  agentKey,
  size = 18,
  label,
  className,
}: AgentMarkProps) {
  const key = normalizedAgentKey(agentKey);
  const identity = agentIdentity(key);
  return (
    <svg
      {...accessibility(label)}
      className={className}
      data-agent-mark="single"
      data-agent-key={key}
      data-agent-color={identity.color}
      data-agent-motif={identity.motif}
      width={Math.round(size * 1.2)}
      height={size}
      viewBox="0 0 24 20"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.15"
      strokeLinecap="square"
      strokeLinejoin="round"
      focusable="false"
    >
      <Face identity={identity} />
    </svg>
  );
}

export function AgentMarkCluster({
  agentKeys,
  size = 18,
  label,
  className,
}: AgentMarkClusterProps) {
  const keys = agentKeys?.filter(Boolean).slice(0, 3) ?? [];
  const visibleKeys = keys.length > 0 ? keys : ["claude", "grok", "placeholder"];
  const faceWidth = 24;
  const gap = 2;
  const viewWidth = visibleKeys.length * faceWidth + Math.max(0, visibleKeys.length - 1) * gap;
  return (
    <svg
      {...accessibility(label)}
      className={className}
      data-agent-mark="cluster"
      data-agent-count={keys.length}
      width={Math.round(size * (viewWidth / 20))}
      height={size}
      viewBox={`0 0 ${viewWidth} 20`}
      fill="none"
      stroke="currentColor"
      strokeWidth="2.15"
      strokeLinecap="square"
      strokeLinejoin="round"
      focusable="false"
    >
      {visibleKeys.map((key, index) => (
        <Face key={`${key}-${index}`} identity={agentIdentity(key)} x={index * (faceWidth + gap)} />
      ))}
    </svg>
  );
}
